"use client";

import React from "react";
import { Building2, MonitorPlay, FileText, CircleCheck as CheckCircle2, Clock, Users, Award, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const trainingModalities = [{
  id: "onsite",
  title: "On-Site Laboratory Training",
  badge: "Hands-On",
  description: "In-person sessions directly at your analytical facility or manufacturing floor across Ireland and the UK.",
  duration: "1–3 Days Intensive",
  audience: "Lab Analysts & QC Scientists",
  certification: "Full Training Certificate",
  points: ["Live instrument operation & calibration", "Method execution on your active equipment", "Routine maintenance & error recovery", "Hardware safety & compliance walkthroughs"],
  highlight: true,
  actionText: "Request On-Site Training"
}, {
  id: "remote",
  title: "Remote Interactive Sessions",
  badge: "Flexible Schedule",
  description: "Live, instructor-led digital workshops covering software suites, data integrity, and analytical workflows.",
  duration: "Modular (2–4 Hour Blocks)",
  audience: "Multi-site Teams & Supervisors",
  certification: "Digital Attendance Record",
  points: ["Live screen-share software workflows", "Real-time sample data interpretation", "Interactive Q&A with technical specialists", "Session recordings provided for revision"],
  highlight: false,
  actionText: "Book Remote Session"
}, {
  id: "documentation",
  title: "Documentation & Protocol Packs",
  badge: "Self-Paced Reference",
  description: "Standard operating procedures, qualification guides, and custom training manuals tailored to your instrument setup.",
  duration: "Continuous Access",
  audience: "Quality Assurance & New Starters",
  certification: "SOP Verification Guides",
  points: ["Customised step-by-step SOP documents", "IQ/OQ/PQ support guidelines & checklists", "Troubleshooting flowcharts & quick guides", "Regulatory audit-ready training records"],
  highlight: false,
  actionText: "Explore Documentation"
}];
const benefitsList = [{
  title: "Regulated Compliance",
  desc: "Designed to meet stringent GMP, GLP, and ISO standard requirements."
}, {
  title: "Application Specialists",
  desc: "Taught by experienced analytical chemists and instrumentation engineers."
}, {
  title: "Tailored Agendas",
  desc: "Curriculums matched to your specific instrumentation and laboratory methods."
}];
export default function Modalities() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: shouldReduceMotion ? 0 : i * 0.12,
        duration: 0.45,
        ease: [0.21, 0.47, 0.32, 0.98]
      }
    })
  };
  return <section id="trainingmodalities" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3">
            Training & Knowledge Transfer
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
            Flexible Training Formats for Regulated Laboratories
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Ensure your analysts, operators, and quality teams achieve operational confidence on analytical instrumentation through practical, compliant learning delivery.
          </p>
        </div>

        {/* Modalities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {trainingModalities.map((item, index) => {
          return <motion.div key={item.id} data-index={index} custom={index} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-50px"
          }} variants={cardVariants} className="flex flex-col h-full">
                <Card className={`flex flex-col justify-between h-full rounded-xl border transition-shadow duration-200 ${item.highlight ? "border-primary/40 shadow-md ring-1 ring-primary/20 bg-card" : "border-border shadow-xs hover:shadow-md bg-card"}`}>
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary">
                        {item.id === "onsite" && <Building2 className="size-5" />}
                        {item.id === "remote" && <MonitorPlay className="size-5" />}
                        {item.id === "documentation" && <FileText className="size-5" />}
                      </div>
                      <Badge variant={item.highlight ? "default" : "outline"} className="text-xs">
                        {item.badge}
                      </Badge>
                    </div>

                    <div>
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="mt-2 text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* Metadata specs */}
                    <div className="space-y-2.5 pt-2 border-t border-border/60 text-xs">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="size-3.5 text-primary shrink-0" />
                        <span><strong>Duration:</strong> {item.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="size-3.5 text-primary shrink-0" />
                        <span><strong>Audience:</strong> {item.audience}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Award className="size-3.5 text-primary shrink-0" />
                        <span><strong>Outcome:</strong> {item.certification}</span>
                      </div>
                    </div>

                    {/* Feature points */}
                    <div className="space-y-2 pt-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        What is covered
                      </p>
                      <ul className="space-y-2">
                        {item.points.map((point, pIndex) => <li key={pIndex} data-index={pIndex} className="flex items-start gap-2 text-sm text-card-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span className="leading-snug">{point}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-4 mt-auto border-t border-border/60">
                    <Button asChild variant={item.highlight ? "default" : "outline"} className="w-full justify-center group">
                      <Link to="/ContactPage" className="flex items-center gap-2">
                        <span>{item.actionText}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Value Prop Banner */}
        <div className="mt-12 md:mt-16 p-6 md:p-8 rounded-xl bg-secondary border border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefitsList.map((benefit, bIndex) => <div key={bIndex} data-index={bIndex} className="space-y-1.5">
                <h4 className="text-base font-semibold text-secondary-foreground font-default">
                  {benefit.title}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {benefit.desc}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}